import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, NavLink, Outlet } from 'react-router-dom';
import {
  Table,
  useQueryState,
  Filters,
  useFilters,
  BaseTransaction,
  useGetList,
  usePrevious,
  PermissionEnum,
  ButtonPrimary,
  ButtonSize,
  IconExpand,
  SchemaFilterType,
  formatDate,
  SelectItemProps,
  formatBalance,
  ButtonPseudo,
  IconDownload,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setTakeCount as setStoreTakeCount,
  clearPaymentOrdersList,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import ROUTES from '../../routes';
import {
  columns,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/list';
import {
  useLazyGetPaymentsOrdersQuery,
  useExportCashCollectionTransactionMutation,
} from './store/services';
import { PaymentOrder, StatusTitle } from './types';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';

import cn from './styles.module.css';

const formatPaymentOrdersList = ({ list }: { list: PaymentOrder[] }) => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, any> = {
      currencyId: <div>{result?.currency?.name || ''}</div>,
      orderCreatedAt: (
        <div>{formatDate(result?.orderCreated as string, true)}</div>
      ),
      sum: <div>{formatBalance(result.sum, result.currency)}</div>,
      recepientAccountId: (
        <>
          <div>{result?.recepientAccount?.description}</div>
          <div className={cn.accNumber}>
            {result?.recepientAccount?.fullNumber}
          </div>
        </>
      ),
      // @ts-ignore
      status: <div>{StatusTitle[item.status as StatusTitle]}</div>,
      orderNumber: <div>{result.orderNumber || '-'}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CashCollectionList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const {
    transactionsDictionaries: { currencies, activePermissions },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const dispatch = useAppDispatch();
  const {
    root: { userId },
    cashCollection: {
      list: { transactions, pagination, takeCount, transactionsErrorsList },
    },
  } = useAppSelector((state) => state);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const [getPaymentsOrders, { isLoading, isFetching }] =
    useLazyGetPaymentsOrdersQuery();
  const [
    exportTransactions,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportCashCollectionTransactionMutation();

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList: getPaymentsOrders,
      onFilterChange,
      onSortChange,
      schema,
    });

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    onClear,
  } = useGetAccountsAutocompleteList('&isClientAccount=true');

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'recepientAccountId') {
      onSearchRecipientAccounts(searchAutocomplete);
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    const data = formatPaymentOrdersList({
      list: transactions as PaymentOrder[],
    });

    return data;
  }, [transactions]);

  const onExportClick = async () => {
    const exportFilters: Record<string, string | number | string[]> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor' && item !== 'take') {
          if (item !== 'cursor') {
            if (
              item === 'status' ||
              item === 'type' ||
              item === 'pspPartnerId' ||
              item === 'organizationId' ||
              item === 'signedByOrganizationId' ||
              item === 'recepientAccountId' ||
              item === 'pspPartnerAccountId' ||
              item === 'currencyId'
            ) {
              exportFilters[item] = filters[item].value;
            } else if (item === 'orderCreatedAt') {
              const orderCreatedAt = filters[item].value;

              const [from, to] = orderCreatedAt;

              exportFilters.orderCreatedAtFrom = from;

              if (to) {
                exportFilters.orderCreatedAtTo = to;
              }
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          }
        }
      });
    }

    await exportTransactions(exportFilters);
  };

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(takeCount as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    setValues('currencyId', currencies as SelectItemProps[]);
    setValues('recepientAccountId', recipientAccounts);
  }, [currencies, recipientAccounts]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortProp &&
      activePermissions
    ) {
      if (
        activePermissions &&
        activePermissions?.includes(PermissionEnum.PAYMENTS_ORDERS_LIST)
      ) {
        let filtersValues: any = {
          take: {
            value: [(takeCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        const savedFiltersJSON = localStorage.getItem(
          'cash-collection-filters',
        );

        let parsedSavedFilters: {
          id: string;
          name: string;
          value: Record<string, any>;
          default?: boolean;
        }[];

        let defaultSavedFilter;

        if (!Object.keys(filters)?.length) {
          if (savedFiltersJSON) {
            parsedSavedFilters = JSON.parse(savedFiltersJSON);

            if (parsedSavedFilters && parsedSavedFilters?.length) {
              const currentUserParsedFilters = parsedSavedFilters?.filter(
                (item: any) => item.employeeId === userId,
              );

              if (
                currentUserParsedFilters &&
                currentUserParsedFilters?.length
              ) {
                defaultSavedFilter = currentUserParsedFilters?.find(
                  (item) => item.default,
                );
              }
            }
          }

          if (defaultSavedFilter) {
            setSelectedSavedFilter(defaultSavedFilter.id);

            filtersValues = {
              ...filtersValues,
              ...defaultSavedFilter?.value,
            };
          } else {
            filtersValues = {
              ...filtersValues,
              ...filters,
            };
          }
        } else {
          filtersValues = {
            ...filtersValues,
            ...filters,
          };
        }

        getListAction({
          filters: filtersValues,
          sort,
        });
      }
    }
  }, [filters, sort, transactions, takeCount, userId, activePermissions]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'payments-orders',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('cash-collection-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  useEffect(
    () => () => {
      dispatch(clearPaymentOrdersList());
    },
    [dispatch],
  );

  let content;

  if (activePermissions?.includes(PermissionEnum.PAYMENTS_ORDERS_LIST)) {
    content = (
      <div className={cn.cashCollection} ref={scrollRef}>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={(name) => {
            onSort(name);
          }}
          filters={filters}
          filtersComponent={filtersComponent}
          sort={sort}
          columnsLabels={columnsLabels}
          onFilterChange={(values) => {
            setSelectedSavedFilter('');

            onFilter({
              ...values,
              take: {
                value: [(takeCount as number)?.toString()],
                type: SchemaFilterType.STRING,
              },
            });
          }}
          handleSubmit={handleSubmit}
          isLoading={isLoading || takeCount === 0 || isFetching}
          take={(takeCount as number)?.toString()}
          setPage={(data) => {
            setPage({
              ...data,
              filters: {
                ...filters,
                take: {
                  value: [(takeCount as number)?.toString()],
                  type: SchemaFilterType.STRING,
                },
              },
            });
          }}
          onClearAutoComplete={() => onClear()}
          pagination={pagination}
          topContent={
            <div>
              {activePermissions?.includes(
                PermissionEnum.PAYMENTS_ORDERS_CREATE_ACTION,
              ) && (
                <NavLink to={`${ROUTES.CASH_COLLECTION_CREATE}${search || ''}`}>
                  <ButtonPrimary
                    text="Create Cash collection"
                    size={ButtonSize.SMALL}
                    icon={<IconExpand />}
                  />
                </NavLink>
              )}
              {activePermissions?.includes(
                PermissionEnum.PAYMENTS_ORDERS_LIST,
              ) && (
                <ButtonPseudo
                  text={exportIsLoading ? 'Loading...' : 'Export transactions'}
                  icon={<IconDownload />}
                  className={`button-white ${cn.exportButton}`}
                  size={ButtonSize.SMALL}
                  onClick={onExportClick}
                />
              )}
            </div>
          }
          hiddedAllFilterTags={Boolean(selectedSavedFilter)}
          error={transactionsErrorsList || errorList}
          paginationDisabled={isFetching || isLoading}
          isFetching={isFetching}
          savedFilters={savedFilters}
          setSavedFilters={setSavedFilters}
          savedFiltersKey="cash-collection-filters"
          selectedSavedFilter={selectedSavedFilter}
          onSavedFilterClick={onSavedFilterClick}
          setSelectedSavedFilter={setSelectedSavedFilter}
          userId={userId}
          clickable
        />
        <Outlet />
      </div>
    );
  } else {
    content = <PermissionDenied />;
  }

  return content;
};

export default CashCollectionList;
